import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Network } from "lucide-react";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import CorrelationTab from "./CorrelationTab";
import SubLedgerTab from "./SubLedgerTab";
import TradePayableTab from "./TradePayableTab";
import InventoryTab from "./InventoryTab";

const TAB_BY_SUFFIX = {
  "sub-ledger":    "sub-ledger",
  "trade-payable": "trade-payable",
  "inventory":     "inventory",
};

export default function DigitalAudit() {
  const { current } = useEngagement();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const suffix = location.pathname.split("/").slice(-1)[0];
  const active = TAB_BY_SUFFIX[suffix] || "correlation";

  const onTabChange = (v) => {
    const map = {
      "correlation":   "/digital-audit/correlation",
      "sub-ledger":    "/digital-audit/sub-ledger",
      "trade-payable": "/digital-audit/trade-payable",
      "inventory":     "/digital-audit/inventory",
    };
    navigate(map[v] || "/digital-audit/correlation");
  };

  return (
    <div className="space-y-4 min-w-0 w-full" data-testid="digital-audit-page">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Network className="h-4 w-4" /> {t("daPage.title")}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("daPage.desc")}
          </p>
        </CardHeader>
      </Card>

      {!current && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {t("daPage.selectEng")}
        </div>
      )}

      {current && (
        <Tabs value={active} onValueChange={onTabChange}>
          <TabsList data-testid="digital-audit-subtabs"
                     className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="correlation"
                          data-testid="digital-audit-tab-correlation"
                          className="text-base font-bold px-4 py-2">
              {t("daPage.correlation")}
            </TabsTrigger>
            <TabsTrigger value="sub-ledger"
                          data-testid="digital-audit-tab-sub-ledger"
                          className="text-base font-bold px-4 py-2">
              {t("daPage.subLedgerTab")}
            </TabsTrigger>
            <TabsTrigger value="trade-payable"
                          data-testid="digital-audit-tab-trade-payable"
                          className="text-base font-bold px-4 py-2">
              {t("daPage.tradePayableTab")}
            </TabsTrigger>
            <TabsTrigger value="inventory"
                          data-testid="digital-audit-tab-inventory"
                          className="text-base font-bold px-4 py-2">
              {t("daPage.inventoryTab")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="correlation"><CorrelationTab /></TabsContent>
          <TabsContent value="sub-ledger"><SubLedgerTab /></TabsContent>
          <TabsContent value="trade-payable"><TradePayableTab /></TabsContent>
          <TabsContent value="inventory"><InventoryTab /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
